/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

// Chakra imports
import {
    Box,
    Icon,
    IconButton,
    Spinner,
    SimpleGrid,
    useColorModeValue,
    useDisclosure,
    ModalOverlay,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Checkbox,
    Textarea,
    Button,
    Heading,
    Modal,
    ModalBody,
    ModalHeader,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    Text,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
    Center,
} from "@chakra-ui/react";

// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect, useMemo } from "react";
import {
    MdMedicalServices,
    MdPerson,
    MdLocalPharmacy,
    MdFileDownload,
    MdAddCircleOutline,
} from "react-icons/md";
import { PiTestTubeFill } from "react-icons/pi";
import { DeleteIcon, InfoIcon, SearchIcon } from "@chakra-ui/icons";
import {
    useGetPatientListQuery,
    useCreatePatientMutation,
    useDeletePatientMutation,
    useSearchPatientsQuery,
    useExportPatientDataMutation,
} from "store";
import { useForm } from "react-hook-form";
import { differenceInYears } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { next } from "stylis";

function PaginatedTable({ columns, initialPageSize = 10 }) {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchDateQuery, setDateSearchQuery] = useState("");
    
    const [triggeredSearchQuery, setTriggeredSearchQuery] = useState(""); // Store triggered search query
    

    const { data: patients, isFetching, error } = triggeredSearchQuery
        ? useSearchPatientsQuery({
              page: pageIndex + 1,
              pageSize,
              search: triggeredSearchQuery,
          })
        : useGetPatientListQuery({ page: pageIndex + 1, pageSize });

    // const { data: patients, isFetching, error } = useGetPatientListQuery(
    //     { page: pageIndex + 1, pageSize },
    //     {
    //         keepPreviousData: true,
    //     }
    // );

    // Safely handle undefined patients and default to last page as 0
    const lastPage =
        patients && patients.count
            ? Math.ceil(patients.count / pageSize) - 1
            : 0;
    const currentPage = pageIndex + 1;

    if (error) return <Text color="red.500">Error: {error.message}</Text>;
    if (isFetching) return <Spinner size="lg" />;

    const displayedPatients = patients?.results || [];

    const handleSearch = () => {
        setTriggeredSearchQuery(searchQuery); // Set the search query to be used in the API call
        setSearchQuery(""); // Optionally clear the input field after search
        setDateSearchQuery(""); // Optionally clear the input field after search
    };

    const handleDateSearch = () => {
        setTriggeredSearchQuery(searchDateQuery); // Set the search query to be used in the API call
        setSearchQuery(""); // Optionally clear the input field after search
        setDateSearchQuery(""); // Optionally clear the input field after search
    };

    const handleReset = () => {
        handleSearch();
    };

    return (
        <Box overflowX="auto">
            {/* Search input */}
            <Flex
                mb="6"
                justifyContent="space-between"
                alignItems="center"
                wrap="wrap"
                p="4"
                borderWidth="1px"
                borderColor="teal.200"
                borderRadius="md"
                boxShadow="md"
            >
                <Flex
                    alignItems="center"
                    flex="1"
                    justifyContent={{ base: "center", md: "flex-start" }}
                    mt={{ base: 2, md: 0 }}
                >
                    <InputGroup
                        width={{ base: "100%", md: "300px" }}
                        borderWidth="2px"
                        borderColor="teal.500"
                        borderRadius="md"
                        mr={{ base: 0, md: 4 }}
                    >
                        <InputLeftElement pointerEvents="none">
                            <Icon as={SearchIcon} color="teal.500" />
                        </InputLeftElement>
                        <Input
                            placeholder="Enter surname to search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            borderRadius="md"
                            p="4"
                            pl="10"
                            _placeholder={{ color: "teal.300" }}
                        />
                    </InputGroup>
                    <Button
                        onClick={handleSearch}
                        colorScheme="teal"
                        size="md"
                        borderRadius="md"
                        px="6"
                        mr="2"
                        _hover={{ bg: "teal.600" }}
                    >
                        Search by Surname
                    </Button>
                    <Button
                        onClick={handleReset}
                        colorScheme="gray"
                        size="md"
                        borderRadius="md"
                        px="6"
                        _hover={{ bg: "gray.600" }}
                    >
                        Reset
                    </Button>
                </Flex>
                <Flex
                    alignItems="center"
                    flex="1"
                    justifyContent={{ base: "center", md: "flex-start" }}
                    mt={{ base: 2, md: 0 }}
                >
                    <InputGroup
                        width={{ base: "100%", md: "300px" }}
                        borderWidth="2px"
                        borderColor="teal.500"
                        borderRadius="md"
                        mr={{ base: 0, md: 4 }}
                    >
                        <InputLeftElement pointerEvents="none">
                            <Icon as={SearchIcon} color="teal.500" />
                        </InputLeftElement>
                        <Input
                            placeholder="Enter date to search"
                            value={searchDateQuery}
                            onChange={(e) => setDateSearchQuery(e.target.value)}
                            borderRadius="md"
                            p="4"
                            pl="10"
                            _placeholder={{ color: "teal.300" }}
                        />
                    </InputGroup>
                    <Button
                        onClick={handleDateSearch}
                        colorScheme="teal"
                        size="md"
                        borderRadius="md"
                        px="6"
                        mr="2"
                        _hover={{ bg: "teal.600" }}
                    >
                        Search by Date
                    </Button>
                    <Button
                        onClick={handleReset}
                        colorScheme="gray"
                        size="md"
                        borderRadius="md"
                        px="6"
                        _hover={{ bg: "gray.600" }}
                    >
                        Reset
                    </Button>
                </Flex>
            </Flex>
            {triggeredSearchQuery && (
                <Text mb="4" color="teal.500" fontSize="md">
                    Found {patients?.count || 0} results for "{triggeredSearchQuery}"
                </Text>
            )}
        
            <Table variant="striped" colorScheme="teal">
                <Thead>
                    <Tr>
                        {columns.map((column) => (
                            <Th key={column.id}>{column.header}</Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {displayedPatients.length > 0 ? (
                        displayedPatients.map((row, i) => (
                            <Tr key={i}>
                                {columns.map((column) => (
                                    <Td key={column.id}>
                                        {typeof column.accessor === "function"
                                            ? column.accessor(row)
                                            : row[column.accessor]}
                                    </Td>
                                ))}
                            </Tr>
                        ))
                    ) : (
                        <Tr>
                            <Td colSpan={columns.length} textAlign="center">
                                No patients available
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
            <Flex justifyContent="space-between" alignItems="center" p="4">
                <Button
                    onClick={() => setPageIndex(0)}
                    disabled={pageIndex === 0}
                >
                    {"<<"}
                </Button>
                <Button
                    onClick={() => setPageIndex((old) => Math.max(old - 1, 0))}
                    disabled={pageIndex === 0}
                >
                    {"<"}
                </Button>
                <Text>
                    Page {currentPage} of {lastPage + 1}
                </Text>
                <span>
                    | Go to page:{" "}
                    <Input
                        type="number"
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                            setPageIndex(page);
                        }}
                        style={{ width: "100px" }}
                    />
                </span>
                <Button
                    onClick={() =>
                        setPageIndex((old) => Math.min(old + 1, lastPage))
                    }
                    disabled={pageIndex >= lastPage}
                >
                    {">"}
                </Button>
                <Button
                    onClick={() => setPageIndex(lastPage)}
                    disabled={pageIndex >= lastPage}
                >
                    {">>"}
                </Button>
                <Select
                    ml="4"
                    width="auto"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // Reset to the first page after changing page size
                    }}
                >
                    {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </Select>
            </Flex>
        </Box>
    );
}

function Patients() {
    const { data: patients, isFetching, error } = useGetPatientListQuery({
        page: 1,
        pageSize: 10,
    });

    const patientCount = patients ? patients.count : 0;
    const [
        deletePatient,
        { isLoading: isDeleting },
    ] = useDeletePatientMutation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const [patientToDelete, setPatientToDelete] = useState(null);
    const toast = useToast();

    const [exportPatientData] = useExportPatientDataMutation();
    const [createPatient, results] = useCreatePatientMutation();
    // const [weight, setWeight] = useState(0);
    // const [height, setHeight] = useState(0);
    // const [bmi, setBmi] = useState(0);
    // const [dob, setDob] = useState("");
    // const [age, setAge] = useState("");

    const handleExport = (format) => {
        exportPatientData(format)
            .unwrap()
            .then(() => console.log("Download started"))
            .catch((error) => console.error("Download failed:", error));
    };

    const handleDetailsClick = (patientId) => {
        navigate(`/patients/${patientId}/details`);
    };

    const handleDeleteClick = (patientId) => {
        setPatientToDelete(patientId);
        onOpen();
    };

    const confirmDelete = async () => {
        try {
            if (!patientToDelete) return;

            // assuming deletePatient is an RTK Query mutation hook:
            await deletePatient(patientToDelete).unwrap();

            toast({
                title: "Patient deleted",
                description: "The patient record has been removed.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            setPatientToDelete(null);
            onClose();
        } catch (error) {
            toast({
                title: "Error deleting patient",
                description:
                    error?.data?.detail ||
                    error?.error ||
                    "An unexpected error occurred.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleVisit = (patientId) => {
        navigate(`/patients/${patientId}/visits`);
    };

    const handlePrecription = (patientId) => {
        navigate(`/patients/${patientId}/prescriptions`);
    };
    const handleLab = (patientId) => {
        navigate(`/patients/${patientId}/labs`);
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
        watch,
    } = useForm({
        defaultValues: {
            surname: "",
            name: "",
            address: "",
            sex: "",
            telephone_number: 0,
            date_of_birth: "",
            marital_status: "",
            next_of_kin: "",
            relation_nok: "",
            telephone_nok: 0,
            nationality: "",
            weight: 0.0,
            height: 0.0,
            is_new: false,
            complaint: "",
            occupation: "",
            diabetes: false,
            heart_murmur: false,
            deep_vein_thrombosis: false,
            high_blood_pressure: false,
            pnuemonia: false,
            prostate_problems: false,
            high_cholesterol: false,
            pulmonary_embolism: false,
            anemia: false,
            hypothyroidism: false,
            hyperthyroidism: false,
            asthma: false,
            juandice: false,
            emphysema_bronchitis: false,
            hepatitis: false,
            stroke: false,
            stomach_peptic_ulcer: false,
            lupus_erythematous: false,
            epilepsy: false,
            rheutamic_fever: false,
            congestive_heart_failure: false,
            cataracts: false,
            tuberculosis: false,
            kidney_disease: false,
            angina: false,
            hiv_aids: false,
            colitis: false,
            kidney_stones: false,
            psychiatric_illness: false,
            cancer_type: "",
            other: "",
            drug_allergy_details: "",
            food_allergy_details: "",
        },
    });

    const [show, setShow] = useState(false);

    // watch your fields
    const dob = watch("date_of_birth");
    const feet = parseFloat(watch("heightFeet")) || 0;
    const inches = parseFloat(watch("heightInches")) || 0;
    const weight = parseFloat(watch("weight")) || 0;

    const totalInches = feet * 12 + inches;

    const age = dob ? differenceInYears(new Date(), new Date(dob)) : "";

    const bmi =
        totalInches > 0 && weight > 0
            ? ((weight * 703) / Math.pow(totalInches, 2)).toFixed(2)
            : "";

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const capitalizeFirstLetter = (str) => {
        return str
            .split(" ")
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ");
    };

    const onSubmit = async (data) => {
        const feet = Number(data.heightFeet) || 0;
        const inches = Number(data.heightInches) || 0;
        const totalInches = feet * 12 + inches;

        const payload = {
            ...data,
            height: totalInches,
        }
        delete payload.heightFeet;
        delete payload.heightInches;

        try {
            await createPatient(payload).unwrap();
            toast({
                title: "Patient created.",
                description: "Patient has been successfully created.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            reset();
            handleClose();
        } catch (error) {
            const apiErrors = error?.data;

            if (
                apiErrors?.non_field_errors?.some((msg) =>
                    msg.toLowerCase().includes("unique")
                )
            ) {
                toast({
                    title: "Duplicate patient.",
                    description: "This patient already exists.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Error creating patient.",
                    description:
                        (apiErrors &&
                            Object.values(apiErrors).flat().join(", ")) ||
                        "An error occurred while creating the patient.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        }
    };

    // useEffect(() => {
    //     if (dob) {
    //         const calculatedAge = differenceInYears(new Date(), new Date(dob));
    //         setAge(calculatedAge);
    //     } else {
    //         setAge("");
    //     }
    // }, [dob]);

    // useEffect(() => {
    //     if (weight > 0 && height > 0) {
    //         const heightInFeet = height * 0.3048; // converting feet to meters
    //         const calculatedBmi = weight / (heightInFeet * heightInFeet);
    //         setBmi(calculatedBmi.toFixed(2)); // limiting BMI to 2 decimal places
    //     }
    // }, [weight, height]);

    const columns = useMemo(
        () => [
            {
                id: "surname",
                header: "Surname",
                accessor: (row) => row.surname,
            },
            { id: "name", header: "Name", accessor: (row) => row.name },
            {
                id: "address",
                header: "Address",
                accessor: (row) => row.address,
            },
            {
                id: "visits",
                header: "Visits",
                accessor: (row) => (
                    <Button
                        aria-label="View visits"
                        leftIcon={<MdMedicalServices />}
                        onClick={() => handleVisit(row.id)}
                    >
                        Visits
                    </Button>
                ),
            },
            {
                id: "prescriptions",
                header: "Prescriptions",
                accessor: (row) => (
                    <Button
                        aria-label="View prescriptions"
                        leftIcon={<MdLocalPharmacy />}
                        onClick={() => handlePrecription(row.id)}
                    >
                        Prescriptions
                    </Button>
                ),
            },
            {
                id: "labs",
                header: "Labs",
                accessor: (row) => (
                    <Button
                        aria-label="View Labs"
                        leftIcon={<PiTestTubeFill />}
                        onClick={() => handleLab(row.id)}
                    >
                        Labs
                    </Button>
                ),
            },
            {
                id: "actions",
                header: "Actions",
                accessor: (row) => (
                    <Box>
                        <IconButton
                            aria-label="View details"
                            icon={<InfoIcon />}
                            onClick={() => handleDetailsClick(row.id)}
                            mr="2"
                        />
                        <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            onClick={() => handleDeleteClick(row.id)}
                            colorScheme="red"
                        />
                    </Box>
                ),
            },
        ],
        []
    );

    // Chakra Color Mode
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const [scrollBehavior, setScrollBehavior] = React.useState("inside");

    return (
        <>
            <Helmet>
                <title>Patient List</title>
            </Helmet>
            <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
                <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                    gap="20px"
                    mb="20px"
                >
                    {isFetching ? (
                        <Box display="flex" justifyContent="center">
                            <Spinner size="xl" />
                        </Box>
                    ) : error ? (
                        <p>Error</p>
                    ) : (
                        <MiniStatistics
                            startContent={
                                <IconBox
                                    w="56px"
                                    h="56px"
                                    bg={boxBg}
                                    icon={
                                        <Icon
                                            w="32px"
                                            h="32px"
                                            as={MdPerson}
                                            color={brandColor}
                                        />
                                    }
                                />
                            }
                            name="Total Patients"
                            value={
                                isFetching ? (
                                    <Spinner size="lg" />
                                ) : error ? (
                                    <Text>Error fetching patients</Text>
                                ) : (
                                    patientCount
                                )
                            }
                        />
                    )}
                </SimpleGrid>

                {/* Delete Modal */}
                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalBody>
                            <Text>
                                Are you sure you want to delete this patient?
                            </Text>
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                isLoading={isDeleting}
                            >
                                Delete
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
                {/* End Delete Modal */}

                {/* Export data Modal */}
                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Export Data</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            Select the format to export the data:
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                colorScheme="blue"
                                mr={3}
                                onClick={() => handleExport("csv")}
                            >
                                Export as CSV
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={() => handleExport("excel")}
                            >
                                Export as Excel
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
                {/* End Export data Modal */}

                <Flex justify="flex-end" mb="4">
                    <Button
                        leftIcon={<MdAddCircleOutline />}
                        isLoading={results.isLoading}
                        onClick={handleShow}
                    >
                        New Patient
                    </Button>
                    {/* <Button
                        ml="4"
                        leftIcon={<MdFileDownload />}
                        onClick={onOpen}
                    >
                        Export Data
                    </Button> */}
                </Flex>

                {/* Create modal form */}

                <Modal
                    isOpen={show}
                    onClose={handleClose}
                    isCentered
                    {...patients}
                    closeOnOverlayClick={false}
                    closeOnEsc={false}
                    scrollBehavior={scrollBehavior}
                    size="4xl"
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>New Patient</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Form fields with register and error handling */}
                                {/* General Information */}
                                <Heading as="h5" size="lg" mb={3}>
                                    General Information
                                </Heading>

                                {/* Row 1 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Surname */}
                                    <Box>
                                        <FormControl isInvalid={errors.surname}>
                                            <FormLabel>Surname</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("surname", {
                                                    required: true,
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "surname",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                            {errors.surname && (
                                                <FormErrorMessage>
                                                    Surname is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>

                                    {/* Name */}
                                    <Box>
                                        <FormControl isInvalid={errors.name}>
                                            <FormLabel>Name</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("name", {
                                                    required: true,
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "name",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                            {errors.name && (
                                                <FormErrorMessage>
                                                    Name is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>

                                    {/* Address */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Address</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("address", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "address",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 2 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Sex */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Sex</FormLabel>
                                            <Select {...register("sex")}>
                                                <option value="M">Male</option>
                                                <option value="F">
                                                    Female
                                                </option>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Nationality */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Nationality</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("nationality", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "nationality",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* Telephone Number */}
                                    <Box>
                                        <FormControl
                                            isInvalid={errors.telephone_number}
                                        >
                                            <FormLabel>
                                                Telephone Number
                                            </FormLabel>
                                            <Input
                                                type="tel"
                                                {...register(
                                                    "telephone_number",
                                                    { valueAsNumber: true }
                                                )}
                                            />
                                            {errors.telephone_number && (
                                                <FormErrorMessage>
                                                    Telephone number is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 3 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* D.O.B */}
                                    <Box>
                                        <FormControl
                                            isInvalid={errors.date_of_birth}
                                        >
                                            <FormLabel>Date of Birth</FormLabel>
                                            <Input
                                                type="date"
                                                {...register("date_of_birth", {
                                                    required: true,
                                                })}
                                            />
                                            {errors.date_of_birth && (
                                                <FormErrorMessage>
                                                    Date of Birth is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>

                                    {/* Age */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Age</FormLabel>
                                            <Input
                                                type="text"
                                                value={
                                                    age === ""
                                                        ? ""
                                                        : `${age} years`
                                                }
                                                isDisabled
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* Marital Status */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>
                                                Marital Status
                                            </FormLabel>
                                            <Select
                                                {...register("marital_status")}
                                            >
                                                <option value="S">
                                                    Single
                                                </option>
                                                <option value="M">
                                                    Married
                                                </option>
                                                <option value="W">
                                                    Widowed
                                                </option>
                                                <option value="D">
                                                    Divorced
                                                </option>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 4 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 4 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Weight */}
                                    <Box>
                                        <FormControl isInvalid={errors.weight}>
                                            <FormLabel>Weight (lbs)</FormLabel>
                                            <Input
                                                type="number"
                                                step="any"
                                                {...register("weight", {
                                                    required: true,
                                                })}
                                            />
                                            {errors.weight && (
                                                <FormErrorMessage>
                                                    Weight is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>

                                    {/* Height — Feet */}
                                    <Box>
                                        <FormControl
                                            isInvalid={errors.heightFeet}
                                        >
                                            <FormLabel>Height (ft)</FormLabel>
                                            <Input
                                                type="number"
                                                step="1"
                                                min={0}
                                                {...register("heightFeet", {
                                                    required:
                                                        "Feet is required",
                                                    valueAsNumber: true,
                                                })}
                                            />
                                            <FormErrorMessage>
                                                {errors.heightFeet &&
                                                    errors.heightFeet.message}
                                            </FormErrorMessage>
                                        </FormControl>
                                    </Box>

                                    {/* Height — Inches */}
                                    <Box>
                                        <FormControl
                                            isInvalid={errors.heightInches}
                                        >
                                            <FormLabel>Height (in)</FormLabel>
                                            <Input
                                                type="number"
                                                step="1"
                                                min={0}
                                                max={11}
                                                {...register("heightInches", {
                                                    required:
                                                        "Inches is required",
                                                    valueAsNumber: true,
                                                })}
                                            />
                                            <FormErrorMessage>
                                                {errors.heightInches &&
                                                    errors.heightInches.message}
                                            </FormErrorMessage>
                                        </FormControl>
                                    </Box>

                                    {/* BMI */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>BMI</FormLabel>
                                            <Input
                                                type="text"
                                                value={bmi}
                                                isDisabled
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 5 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* N.O.K */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Next of Kin</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("next_of_kin", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "next_of_kin",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* Relation N.O.K */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>
                                                Relation to Next of Kin
                                            </FormLabel>
                                            <Input
                                                type="text"
                                                {...register("relation_nok", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "relation_nok",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* # of N.O.K */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>
                                                Telephone Number (Next of Kin)
                                            </FormLabel>
                                            <Input
                                                type="tel"
                                                {...register("telephone_nok", {
                                                    valueAsNumber: true,
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 6 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Occupation */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Occupation</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("occupation", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "occupation",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* Is New */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>New Patient</FormLabel>
                                            <Checkbox
                                                {...register("is_new")}
                                                label="Yes"
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Row 7 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 1 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Complaint</FormLabel>
                                            <Textarea
                                                as="textarea"
                                                rows={4}
                                                {...register("complaint", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "complaint",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                <hr className="mb-2" />

                                {/* Medical History */}
                                <Heading as="h5" size="lg" my={3}>
                                    Past Medical History
                                </Heading>

                                {/* Row 8 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Group 1 */}
                                    <div className="col-4">
                                        {/* Diabetes */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Diabetes</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("diabetes")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Heart Mumur */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Heart Murmur</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("heart_murmur")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Deep Vein Thrombosis */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Deep Vein Thrombosis
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "deep_vein_thrombosis"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* HBP */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                High Blood Pressure
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "high_blood_pressure"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Pneumonia */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Pneumonia</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("pneumonia")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Prostate */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Prostate</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "prostate_problems"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* High Cholesterol */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                High Cholesterol
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "high_cholesterol"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Pulmonary Embolism */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Pulmonary Embolism
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "pulmonary_embolism"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Anemia */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Anemia</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("anemia")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Hypothyroidism */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Hypothyroidism
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("hypothyroidism")}
                                                label="Yes"
                                            />
                                        </FormControl>
                                    </div>

                                    {/* Group 2 */}
                                    <div className="col-4">
                                        {/* Asthma */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Asthma</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("asthma")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Juandice */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Juandice</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("jaundice")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Hyperthyroidism */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Hyperthyroidism
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("hyperthyroidism")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Emphysema Bronchitis */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Emphysema Bronchitis
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "emphysema_bronchitis"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Hepatitis */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Hepatitis</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("hepatitis")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Cancer */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Cancer Type (if applicable)
                                            </FormLabel>
                                            <Input
                                                type="text"
                                                {...register("cancer_type")}
                                            />
                                        </FormControl>

                                        {/* Stroke */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Stroke</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("stroke")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Stomach or Peptic Ulcer */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Stomach or Peptic Ulcer
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "stomach_peptic_ulcer"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Systemic lupus
                            erythematous(Lupus) */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Systemic lupus
                                                erythematous(Lupus)
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "lupus_erythematous"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Epilepsy */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Epilepsy</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("epilepsy")}
                                                label="Yes"
                                            />
                                        </FormControl>
                                    </div>

                                    {/* Group 3 */}
                                    <div className="col-4">
                                        {/* Rheumatic Fever */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Rheumatic Fever
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("rheumatic_fever")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Congestive Heart
                            Failure */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Congestive Heart Failure
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "congestive_heart_failure"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Cataracts */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Cataracts</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("cataracts")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Tuberculosis */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Tuberculosis</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("tuberculosis")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Angina */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Angina</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("angina")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Kidney Disease */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Kidney Disease
                                            </FormLabel>
                                            <Checkbox
                                                label="Yes"
                                                type="checkbox"
                                                {...register("kidney_disease")}
                                            />
                                        </FormControl>

                                        {/* HIV/AIDS */}
                                        <FormControl className="mb-3">
                                            <FormLabel>HIV/AIDS</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("hiv_aids")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Colitis */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Colitis</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("colitis")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Kidney Stones */}
                                        <FormControl className="mb-3">
                                            <FormLabel>Kidney Stones</FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register("kidney_stones")}
                                                label="Yes"
                                            />
                                        </FormControl>

                                        {/* Psychiatric Illness */}
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Psychiatric Illness
                                            </FormLabel>
                                            <Checkbox
                                                type="checkbox"
                                                {...register(
                                                    "psychiatric_illness"
                                                )}
                                                label="Yes"
                                            />
                                        </FormControl>
                                    </div>
                                </SimpleGrid>

                                {/* Row 9 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 1 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    <div className="col-12 mb-3">
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Other Medical History (optional)
                                            </FormLabel>
                                            <Input
                                                type="text"
                                                {...register("other", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "other",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </div>
                                </SimpleGrid>

                                <hr className="mb-2" />

                                {/* Allergies */}
                                <Heading as="h5" size="lg" my={3}>
                                    Allergies
                                </Heading>

                                {/* Row 10 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 2 }}
                                    spacing={4}
                                    mb={6}
                                >
                                    {/* Drug Allergies */}
                                    <Box>
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Drug Allergies (optional)
                                            </FormLabel>
                                            <Input
                                                type="text"
                                                {...register(
                                                    "drug_allergy_details",
                                                    {
                                                        onChange: (e) => {
                                                            const capitalizedValue = capitalizeFirstLetter(
                                                                e.target.value
                                                            );
                                                            setValue(
                                                                "drug_allergy_details",
                                                                capitalizedValue,
                                                                {
                                                                    shouldValidate: true,
                                                                }
                                                            );
                                                        },
                                                    }
                                                )}
                                            />
                                        </FormControl>
                                    </Box>

                                    {/* Food Allergies */}
                                    <Box>
                                        <FormControl className="mb-3">
                                            <FormLabel>
                                                Food Allergies (optional)
                                            </FormLabel>
                                            <Input
                                                type="text"
                                                {...register(
                                                    "food_allergy_details",
                                                    {
                                                        onChange: (e) => {
                                                            const capitalizedValue = capitalizeFirstLetter(
                                                                e.target.value
                                                            );
                                                            setValue(
                                                                "food_allergy_details",
                                                                capitalizedValue,
                                                                {
                                                                    shouldValidate: true,
                                                                }
                                                            );
                                                        },
                                                    }
                                                )}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                <Center>
                                    <Box>
                                        <Button
                                            fontSize="sm"
                                            variant="outline"
                                            fontWeight="500"
                                            type="submit"
                                            h="50"
                                            mb="24px"
                                            isLoading={results.isLoading}
                                            loadingText="Loading..."
                                            spinner={<Spinner />}
                                        >
                                            Save
                                        </Button>
                                    </Box>
                                </Center>

                                {/* Display success or error messages based on mutation state */}
                                {/* {results.isSuccess && (
                                <p>Patient created successfully!</p>
                            )}
                            {error && (
                                <p>
                                    Error creating patient:{" "}
                                    {error.data.message}
                                </p>
                            )} */}
                            </form>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* End form */}

                <SimpleGrid
                    columns={{ base: 1, md: 1, xl: 1 }}
                    gap="20px"
                    mb="20px"
                >
                    {isFetching ? (
                        <Box display="flex" justifyContent="center">
                            <Spinner size="xl" />
                        </Box>
                    ) : error ? (
                        <Box>Error fetching patient data</Box>
                    ) : (
                        <PaginatedTable
                            columns={columns}
                            initialPageSize={10}
                        />
                    )}
                </SimpleGrid>
            </Box>
        </>
    );
}

export default Patients;
